import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { store } from "../../store";
import { verifyLogin } from "../util/verifyLogin";
import { getKeyByValue, isUrlIncludesRoutes } from "../util/routeUtils";
import { AUTH_ROUTE_PATHS } from "../../routing/AuthenticationRoutes";
import type { BootUser } from "../../reducers/boot";
import type { Config } from "../../common/interfaces/config";

type UseAuthenticationParams = {
  user: BootUser;
  pathname: string;
  selectedPeriod: string;
  maintenanceConfig?: Config["maintenanceConfig"];
};

/**
 * Hook that handles authentication verification and session management:
 * - Verifies login on route changes
 * - Checks maintenance mode
 *
 * @param user - The user object from boot state (pass from parent to ensure consistent reference)
 * @param pathname - The current pathname
 * @param selectedPeriod - The currently selected period
 * @param maintenanceConfig - The maintenance configuration
 */
export const useAuthentication = ({ user, pathname, selectedPeriod, maintenanceConfig }: UseAuthenticationParams) => {
  const navigate = useNavigate();

  const checkMaintenance = useCallback(() => {
    const { isImpersonation } = store.getState().boot;

    if (
      maintenanceConfig &&
      (maintenanceConfig[getKeyByValue(pathname)!] || maintenanceConfig.appMaintenance) &&
      !isImpersonation
    ) {
      navigate(AUTH_ROUTE_PATHS.MAINTENANCE);
    }
  }, [pathname, maintenanceConfig, navigate]);

  // Check if access_token is active and verify login on route changes
  useEffect(() => {
    checkMaintenance();
    if (!isUrlIncludesRoutes()) {
      if (user && user.access_token) {
        verifyLogin(user);
      }
    }
  }, [pathname, selectedPeriod, user, checkMaintenance]);

  return {
    isAuthenticated: !!user.access_token,
    checkMaintenance,
  };
};

export type UseAuthenticationReturn = {
  isAuthenticated: boolean;
  checkMaintenance: () => void;
};
